import {
  type ClassificationLevel,
  deriveLevelId,
  egressFloorAllowed,
  readersFloorSchema,
} from "@esi/shared";
import {
  ArrowDown,
  ArrowUp,
  Ban,
  ChevronDown,
  Download,
  FilePlus,
  Globe,
  MessageSquare,
  Plus,
  ShieldCheck,
  Trash2,
  Users,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { formatList } from "../../i18n/format.ts";
import {
  choiceConsequence,
  impactContext,
  impactIcon,
  levelImpact,
  levelSummary,
} from "./impact.ts";
import type { ReviewSentence, Usage } from "./review.ts";
import { editProblems, type LevelProblem } from "./validation.ts";

/**
 * The classification levels editor (openspec change classification-editor, design D1–D6): an
 * ordered list, least to most sensitive, one compact row per level; a row opens in place to its
 * name, who can read, downloads and outbound tools, each with its consequence, beside the level's
 * "In practice". Add, move, remove and undo; ids are derived from a new level's name and never
 * shown. It emits the scheme as it stands (problems included): the setting row validates it with
 * the same rules and holds Save.
 */

type Floor = ClassificationLevel["readersFloor"];
const FLOORS = readersFloorSchema.options;

/** A level being edited; `id` is null until a new level's id is derived from its name. */
interface Draft {
  key: string;
  id: string | null;
  label: string;
  readersFloor: Floor;
  downloadAllowed: boolean;
  egressFloor?: Floor;
}

let nextKey = 0;
const newKey = () => `level-${nextKey++}`;

function toDrafts(value: unknown): Draft[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return (value as ClassificationLevel[]).map((level) => ({
    key: newKey(),
    id: level.id,
    label: level.label,
    readersFloor: level.readersFloor,
    downloadAllowed: level.downloadAllowed,
    ...(level.egressFloor !== undefined && { egressFloor: level.egressFloor }),
  }));
}

/**
 * The scheme the drafts make: a new level gets an id no saved or other level has, nor any id
 * files or default classifications still name (the usage read lists those) (D4).
 */
function toScheme(
  drafts: readonly Draft[],
  saved: readonly ClassificationLevel[],
  used: Iterable<string> = [],
) {
  const taken = new Set([
    ...saved.map((l) => l.id),
    ...drafts.flatMap((d) => (d.id ? [d.id] : [])),
    ...used,
  ]);
  return drafts.map((draft): ClassificationLevel & { key: string } => {
    let id = draft.id;
    if (id === null) {
      id = deriveLevelId(draft.label, taken);
      taken.add(id);
    }
    return {
      key: draft.key,
      id,
      label: draft.label,
      downloadAllowed: draft.downloadAllowed,
      readersFloor: draft.readersFloor,
      ...(draft.egressFloor !== undefined && { egressFloor: draft.egressFloor }),
    };
  });
}

const strip = ({ key: _, ...level }: ClassificationLevel & { key: string }): ClassificationLevel =>
  level;

export function LevelList(props: {
  id: string;
  /** The saved scheme (what the review compares with and ids must not collide with). */
  saved: readonly ClassificationLevel[];
  value: unknown;
  onChange: (value: ClassificationLevel[]) => void;
  /** The effective settings at the tenant (outbound policy, declassification, default level). */
  settings: readonly { key: string; value: unknown }[];
  /**
   * Usage per level id; null from an older API (Remove is then offered, the server's 409
   * guards); undefined while loading or after an error (Remove is held until it is known).
   */
  usage: Usage | undefined;
}) {
  const { t } = useTranslation();
  // Keys computed from the level (impact.ts): typed as plain strings.
  const translateKey = t as unknown as (key: string, values?: object) => string;
  const listId = useId();
  const [drafts, setDrafts] = useState<Draft[]>(() => toDrafts(props.value));
  const [open, setOpen] = useState<string | null>(null);
  const [removed, setRemoved] = useState<{ draft: Draft; index: number }[]>([]);
  const [announcement, setAnnouncement] = useState("");
  const [focus, setFocus] = useState<string | null>(null);
  const buttons = useRef(new Map<string, HTMLElement>());
  useEffect(() => {
    if (focus) {
      buttons.current.get(focus)?.focus();
      setFocus(null);
    }
  }, [focus]);

  const used = Object.keys(props.usage ?? {});
  const scheme = toScheme(drafts, props.saved, used);
  // A new level named before the usage read arrived got its id without the ids files and
  // defaults still name: emit the scheme again once they are known.
  const usedKey = used.join("\n");
  const emitted = useRef(usedKey);
  useEffect(() => {
    if (emitted.current !== usedKey) {
      emitted.current = usedKey;
      if (drafts.some((d) => d.id === null)) {
        props.onChange(scheme.map(strip));
      }
    }
  });
  const problems = editProblems(props.saved, scheme);
  const context = impactContext(scheme, props.settings);
  const say = (s: ReviewSentence) => translateKey(`adminSettings.levels.${s.key}`, s.values);
  const update = (next: Draft[]) => {
    setDrafts(next);
    props.onChange(toScheme(next, props.saved, used).map(strip));
  };
  const edit = (key: string, change: Partial<Draft>) =>
    update(drafts.map((d) => (d.key === key ? { ...d, ...change } : d)));
  const nameOf = (draft: Pick<Draft, "label">) =>
    draft.label.trim() || t("adminSettings.levels.unnamed");

  const move = (index: number, by: -1 | 1) => {
    const target = index + by;
    const draft = drafts[index];
    const other = drafts[target];
    if (!draft || !other) {
      return;
    }
    const next = [...drafts];
    next[index] = other;
    next[target] = draft;
    update(next);
    // Focus stays on the moved level: its button, or the other one at an end.
    const atEnd = by === -1 ? target === 0 : target === next.length - 1;
    setFocus(`${draft.key}-${atEnd ? (by === -1 ? "down" : "up") : by === -1 ? "up" : "down"}`);
    setAnnouncement(
      t("adminSettings.levels.moved", {
        level: nameOf(draft),
        position: target + 1,
        total: next.length,
      }),
    );
  };

  const add = () => {
    const at = open ? drafts.findIndex((d) => d.key === open) + 1 : drafts.length;
    const draft: Draft = {
      key: newKey(),
      id: null,
      label: t("adminSettings.levels.newName"),
      // The strictest settings: only named readers, no download, outbound like its readers.
      readersFloor: "named",
      downloadAllowed: false,
    };
    const next = [...drafts.slice(0, at), draft, ...drafts.slice(at)];
    update(next);
    setOpen(draft.key);
    setFocus(`${draft.key}-name`);
    setAnnouncement(
      t("adminSettings.levels.added", { level: draft.label, position: at + 1, total: next.length }),
    );
  };

  const remove = (index: number) => {
    const draft = drafts[index];
    if (!draft) {
      return;
    }
    update(drafts.filter((_, i) => i !== index));
    setRemoved([...removed, { draft, index }]);
    setOpen(null);
    setFocus("undo");
    setAnnouncement(t("adminSettings.levels.removed", { level: nameOf(draft) }));
  };

  const undo = () => {
    const last = removed.at(-1);
    if (!last) {
      return;
    }
    const at = Math.min(last.index, drafts.length);
    update([...drafts.slice(0, at), last.draft, ...drafts.slice(at)]);
    setRemoved(removed.slice(0, -1));
    setFocus(`${last.draft.key}-toggle`);
    setAnnouncement(
      t("adminSettings.levels.restored", { level: nameOf(last.draft), position: at + 1 }),
    );
  };

  const lastRemoved = removed.at(-1);
  const register = (name: string) => (el: HTMLElement | null) => {
    if (el) {
      buttons.current.set(name, el);
    } else {
      buttons.current.delete(name);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-fg-muted">{t("adminSettings.levels.howItWorks")}</p>
      <p className="text-xs text-fg-muted" aria-hidden>
        {t("adminSettings.levels.leastSensitive")}
      </p>
      <ol
        id={listId}
        aria-label={t("adminSettings.levels.listLabel")}
        className="divide-y divide-line rounded-md border border-line"
      >
        {drafts.map((draft, index) => {
          const level = scheme[index] as ClassificationLevel;
          const found = problems.levels.get(index) ?? [];
          const counts =
            draft.id !== null && props.usage && Object.hasOwn(props.usage, draft.id)
              ? props.usage[draft.id]
              : undefined;
          return (
            <LevelRow
              key={draft.key}
              id={`${props.id}-${draft.key}`}
              draft={draft}
              level={level}
              index={index}
              total={drafts.length}
              open={open === draft.key}
              problems={found}
              files={counts?.documents ?? 0}
              usageKnown={draft.id === null || props.usage !== undefined}
              defaults={counts?.defaults ?? 0}
              summary={levelSummary(level, context).map(say)}
              consequence={(choice) => say(choiceConsequence(choice, level, context))}
              impact={levelImpact(level, context).map((s) => ({
                icon: impactIcon(s),
                text: say(s),
              }))}
              name={nameOf(draft)}
              register={register}
              onToggle={() => setOpen(open === draft.key ? null : draft.key)}
              onMove={(by) => move(index, by)}
              onEdit={(change) => edit(draft.key, change)}
              onRemove={() => remove(index)}
            />
          );
        })}
      </ol>
      <p className="text-xs text-fg-muted" aria-hidden>
        {t("adminSettings.levels.mostSensitive")}
      </p>
      {problems.empty ? (
        <p className="text-xs text-destructive">{t("adminSettings.levels.problems.empty")}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button type="button" size="sm" variant="outline" className="max-sm:h-11" onClick={add}>
          <Plus aria-hidden />
          {t("adminSettings.levels.add")}
        </Button>
        {lastRemoved ? (
          <span className="flex items-center gap-1 text-xs text-fg-secondary">
            {t("adminSettings.levels.removed", { level: nameOf(lastRemoved.draft) })}
            <Button
              ref={register("undo")}
              type="button"
              size="xs"
              variant="link"
              className="h-auto p-0 text-xs max-sm:min-h-11"
              onClick={undo}
            >
              {t("adminSettings.levels.undo")}
            </Button>
          </span>
        ) : null}
      </div>
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}

/**
 * A level's collapsed row (D2, D10), the same in the editor and the read-only views: position,
 * name, summary and file count (on the name's line below `sm`), then `children` (its buttons).
 */
export function LevelHeader(props: {
  position: number;
  name: string;
  summary: readonly string[];
  files: number;
  /** After the summary (the editor's problem count). */
  extra?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const count = props.files > 0 ? t("adminSettings.levels.files", { count: props.files }) : null;
  return (
    <div className="flex min-h-12 items-center gap-3 px-3 py-1.5">
      <span className="w-4 shrink-0 text-xs text-fg-muted tabular-nums">{props.position}</span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 sm:flex-nowrap">
          <span className="text-sm font-medium break-words text-fg sm:min-w-0 sm:truncate">
            {props.name}
          </span>
          {count ? (
            <span className="shrink-0 text-xs text-fg-secondary tabular-nums sm:hidden">
              {count}
            </span>
          ) : null}
        </span>
        <span className="text-xs text-fg-secondary">
          {props.summary.join(" · ")}
          {props.extra}
        </span>
      </div>
      {count ? (
        <span className="shrink-0 text-xs text-fg-secondary tabular-nums max-sm:hidden">
          {count}
        </span>
      ) : null}
      {props.children}
    </div>
  );
}

/** A level's "In practice" list (D2), the same in the editor and the read-only views (D9). */
export function InPractice(props: { id: string; impact: readonly ImpactItem[] }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby={props.id} className="flex min-w-0 flex-col gap-2">
      <h4 id={props.id} className="text-sm font-medium text-fg">
        {t("adminSettings.levels.inPractice")}
      </h4>
      <ul className="flex flex-col gap-2 text-[13px] text-fg-secondary">
        {props.impact.map((item) => {
          const Icon = IMPACT_ICONS[item.icon];
          return (
            <li key={item.text} className="flex items-start gap-2">
              <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-fg-muted" />
              <span>{item.text}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export interface ImpactItem {
  icon: keyof typeof IMPACT_ICONS;
  text: string;
}

/**
 * Phone (D10): the row's icon buttons and the details' controls are 44px targets below `sm`.
 */
export const TOUCH_ICON = "max-sm:size-11";
export const TOUCH_TARGET = "max-sm:min-h-11";

const IMPACT_ICONS = {
  readers: Users,
  download: Download,
  noDownload: Ban,
  outbound: Globe,
  declassification: ShieldCheck,
  default: FilePlus,
  conversation: MessageSquare,
} as const;

function LevelRow(props: {
  id: string;
  draft: Draft;
  level: ClassificationLevel;
  index: number;
  total: number;
  open: boolean;
  problems: readonly LevelProblem[];
  files: number;
  defaults: number;
  /** Whether the counts are known (a saved level's usage loaded, or a new level). */
  usageKnown: boolean;
  summary: string[];
  consequence: (choice: "name" | "readers" | "download" | "outbound") => string;
  impact: ImpactItem[];
  name: string;
  register: (name: string) => (el: HTMLElement | null) => void;
  onToggle: () => void;
  onMove: (by: -1 | 1) => void;
  onEdit: (change: Partial<Draft>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { id, draft, index, total, problems, files, defaults, register } = props;
  const panel = `${id}-panel`;
  const nameProblem = problems.find((p) => p !== "egressStricter");
  const egressProblem = problems.includes("egressStricter");
  // An egress floor only loosens its level: "like its readers" and the floors up to its readers.
  const allowed = FLOORS.filter((f) => egressFloorAllowed(draft.readersFloor, f));
  const egressChoice = draft.egressFloor ?? "readers";
  const inUse = files > 0 || defaults > 0;
  return (
    <li>
      <LevelHeader
        position={index + 1}
        name={props.name}
        summary={props.summary}
        files={files}
        extra={
          problems.length > 0 ? (
            <span className="text-destructive">
              {" · "}
              <span
                aria-hidden
                className="mr-1 inline-block size-1.5 rounded-full bg-destructive align-middle"
              />
              {t("adminSettings.levels.problem", { count: problems.length })}
            </span>
          ) : null
        }
      >
        <span className="flex shrink-0 items-center gap-0.5">
          <Button
            ref={register(`${draft.key}-up`)}
            type="button"
            size="icon-xs"
            variant="ghost"
            className={TOUCH_ICON}
            aria-label={t("adminSettings.levels.moveUp", { level: props.name })}
            disabled={index === 0}
            onClick={() => props.onMove(-1)}
          >
            <ArrowUp aria-hidden />
          </Button>
          <Button
            ref={register(`${draft.key}-down`)}
            type="button"
            size="icon-xs"
            variant="ghost"
            className={TOUCH_ICON}
            aria-label={t("adminSettings.levels.moveDown", { level: props.name })}
            disabled={index === total - 1}
            onClick={() => props.onMove(1)}
          >
            <ArrowDown aria-hidden />
          </Button>
          <Button
            ref={register(`${draft.key}-toggle`)}
            type="button"
            size="icon-xs"
            variant="ghost"
            className={TOUCH_ICON}
            aria-label={t("adminSettings.levels.details", { level: props.name })}
            aria-expanded={props.open}
            aria-controls={panel}
            onClick={props.onToggle}
          >
            <ChevronDown
              aria-hidden
              className={cn("transition-transform", props.open && "rotate-180")}
            />
          </Button>
        </span>
      </LevelHeader>
      {props.open ? (
        <div
          id={panel}
          className="grid gap-5 border-t border-line bg-surface-sunken p-4 @xl:grid-cols-2"
        >
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-name`}>{t("adminSettings.levels.fields.name")}</Label>
              <Input
                id={`${id}-name`}
                ref={register(`${draft.key}-name`)}
                value={draft.label}
                className="max-sm:h-11"
                aria-invalid={nameProblem !== undefined}
                aria-describedby={`${id}-name-help`}
                onFocus={(e) => {
                  if (draft.id === null) {
                    e.currentTarget.select();
                  }
                }}
                onChange={(e) => props.onEdit({ label: e.currentTarget.value })}
                // Enter in the name never submits the row's form (and so never confirms a save).
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
              <p id={`${id}-name-help`} className="text-xs text-fg-muted">
                {nameProblem ? (
                  <span className="text-destructive">
                    {t(`adminSettings.levels.problems.${nameProblem}`)}
                  </span>
                ) : (
                  props.consequence("name")
                )}
              </p>
            </div>
            <fieldset className="flex flex-col gap-2">
              <legend id={`${id}-readers-label`} className="mb-1 text-sm font-medium text-fg">
                {t("adminSettings.levels.fields.readers")}
              </legend>
              <RadioGroup
                className="gap-2"
                value={draft.readersFloor}
                aria-labelledby={`${id}-readers-label`}
                aria-describedby={`${id}-readers-now`}
                onValueChange={(value) => props.onEdit({ readersFloor: value as Floor })}
              >
                {FLOORS.map((floor) => (
                  <div key={floor} className="flex items-start gap-2 max-sm:items-center">
                    <RadioGroupItem
                      id={`${id}-readers-${floor}`}
                      value={floor}
                      className="mt-0.5 max-sm:mt-0"
                    />
                    <Label
                      htmlFor={`${id}-readers-${floor}`}
                      className={cn("flex-1 leading-snug font-normal text-fg", TOUCH_TARGET)}
                    >
                      {t(`adminSettings.levels.readersOption.${floor}`)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <p id={`${id}-readers-now`} className="text-xs text-fg-secondary" aria-live="polite">
                {props.consequence("readers")}
              </p>
            </fieldset>
            <fieldset className="flex flex-col gap-2">
              <legend id={`${id}-download-label`} className="mb-1 text-sm font-medium text-fg">
                {t("adminSettings.levels.fields.download")}
              </legend>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-download`}
                  checked={draft.downloadAllowed}
                  aria-describedby={`${id}-download-now`}
                  onCheckedChange={(on) => props.onEdit({ downloadAllowed: on === true })}
                />
                <Label
                  htmlFor={`${id}-download`}
                  className={cn("flex-1 leading-snug font-normal text-fg", TOUCH_TARGET)}
                >
                  {t("adminSettings.levels.fields.downloadLabel")}
                </Label>
              </div>
              <p id={`${id}-download-now`} className="text-xs text-fg-secondary" aria-live="polite">
                {props.consequence("download")}
              </p>
            </fieldset>
            <fieldset className="flex flex-col gap-2">
              <legend id={`${id}-outbound-label`} className="mb-1 text-sm font-medium text-fg">
                {t("adminSettings.egress.title")}
              </legend>
              <RadioGroup
                className="gap-2"
                value={egressProblem ? "" : egressChoice}
                aria-invalid={egressProblem}
                aria-labelledby={`${id}-outbound-label`}
                aria-describedby={`${id}-outbound-now`}
                onValueChange={(value) =>
                  props.onEdit({ egressFloor: value === "readers" ? undefined : (value as Floor) })
                }
              >
                {(["readers", ...allowed] as const).map((choice) => (
                  <div key={choice} className="flex items-start gap-2 max-sm:items-center">
                    <RadioGroupItem
                      id={`${id}-outbound-${choice}`}
                      value={choice}
                      className="mt-0.5 max-sm:mt-0"
                    />
                    <Label
                      htmlFor={`${id}-outbound-${choice}`}
                      className={cn("flex-1 leading-snug font-normal text-fg", TOUCH_TARGET)}
                    >
                      {t(`adminSettings.egress.${choice}`)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <p id={`${id}-outbound-now`} className="text-xs text-fg-secondary" aria-live="polite">
                {egressProblem ? (
                  <span className="text-destructive">
                    {t("adminSettings.levels.problems.egressStricter")}
                  </span>
                ) : (
                  props.consequence("outbound")
                )}
              </p>
            </fieldset>
            {inUse ? (
              <p className="text-xs text-fg-muted">
                {t("adminSettings.levels.usedBy", {
                  uses: formatList([
                    ...(files > 0 ? [t("adminSettings.levels.usesFiles", { count: files })] : []),
                    ...(defaults > 0
                      ? [t("adminSettings.levels.usesDefaults", { count: defaults })]
                      : []),
                  ]),
                })}{" "}
                {t(
                  `adminSettings.levels.nextStep.${files > 0 && defaults > 0 ? "both" : files > 0 ? "files" : "defaults"}`,
                )}
              </p>
            ) : !props.usageKnown ? null : (
              <div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive max-sm:h-11"
                  onClick={props.onRemove}
                >
                  <Trash2 aria-hidden />
                  {t("adminSettings.levels.remove")}
                </Button>
              </div>
            )}
          </div>
          <InPractice id={`${id}-practice`} impact={props.impact} />
        </div>
      ) : null}
    </li>
  );
}
